import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  inject,
  input,
  OnDestroy,
  OnInit,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTreeModule } from '@angular/material/tree';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { Actions, ofActionDispatched, Store } from '@ngxs/store';
import {
  AuthenticationSelectors,
  ConfigService,
  SetUserRightsSuccess,
  VersionService,
} from '@orion-platform/core';
import { SignalRService } from 'libs/core/src/lib/services/signal-r.service';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { Helper } from '../../helper/helper';
import {
  EnumSideNavTypes,
  MatTreeNode,
  SideNavDataSource,
  SideNavOptions,
} from '../../models/sidebar.model';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [MatTreeModule, MatIconModule, MatButtonModule, RouterModule],
  selector: 'orion-platform-sidebar',
  standalone: true,
  styleUrl: './sidebar.component.scss',
  templateUrl: './sidebar.component.html',
})
export class SidebarComponent implements OnInit, OnDestroy, AfterViewInit {
  private readonly router = inject(Router);
  private readonly configService = inject(ConfigService);
  private readonly store = inject(Store);
  private readonly actions$ = inject(Actions);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly signalRService = inject(SignalRService);
  protected readonly versionService = inject(VersionService);

  private noOfChildren = 0;
  userRights: string[] = [];
  public readonly env = this.configService.getSettings('environment');
  public env2 = signal<string>('');

  childrenAccessor = (node: SideNavOptions): SideNavOptions[] => node.children ?? [];

  dataSource = signal<SideNavOptions[]>([]);

  hoverTimeOut?: ReturnType<typeof setTimeout>;

  isCollapse = input(true);

  nodeData?: MatTreeNode;

  sideNavData: SideNavDataSource = new SideNavDataSource();

  private ngUnsubscribe: Subject<void> = new Subject();

  ngOnInit(): void {
    this.catchSetUserRightsSuccess();
    const menuAccessRights = this.store.selectSnapshot(AuthenticationSelectors.menuAccessRightList);
    this.userRights = menuAccessRights ? menuAccessRights : [];

    this.router.events
      .pipe(
        filter((event): boolean => event instanceof NavigationEnd),
        takeUntil(this.ngUnsubscribe),
      )
      .subscribe((event): void => {
        const { urlAfterRedirects } = event as NavigationEnd;
        this.updateActiveState(urlAfterRedirects);
      });

    this.updateActiveState(this.router.url);

    const tokenValue = this.store.selectSnapshot(AuthenticationSelectors.token);
    if (tokenValue) {
      this.setItemsAccess();
    } else {
      this.signalRService
        .getTokenReady()
        .pipe(takeUntil(this.ngUnsubscribe))
        .subscribe((isTokenReady: boolean): void => {
          if (isTokenReady) {
            this.setItemsAccess();
          }
        });
    }
  }

  ngAfterViewInit(): void {
    this.getEnvironmentFromBrowser();
    this.cdr.detectChanges();
  }

  ngOnDestroy(): void {
    if (this.hoverTimeOut) {
      clearTimeout(this.hoverTimeOut);
    }
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  public setItemsAccess(): void {
    const rawData: SideNavOptions[] = this.sideNavData.sideNavDataSource;

    for (const item of rawData) {
      if (item.children && item.children.length > 0) {
        this.noOfChildren += item.children.length;
      }
    }

    const menuAccessRights = this.store.selectSnapshot(AuthenticationSelectors.menuAccessRightList);
    this.userRights = menuAccessRights ? menuAccessRights : [];
    if (this.userRights && this.userRights.length > 0) {
      const cumulativeMenu = rawData
        .map((item: SideNavOptions): SideNavOptions => {
          if (item.children) {
            const children = item.children.filter((child: SideNavOptions): boolean =>
              this.userRights.includes(child.id ? child.id : child.name),
            );
            return { ...item, children };
          }
          return item;
        })
        .filter((item: SideNavOptions): boolean =>
          item.children ? item.children.length > 0 : true,
        );

      this.dataSource.set(cumulativeMenu);
    } else {
      this.dataSource.set(rawData);
    }
  }

  public hasChild(_: number, node: SideNavOptions): boolean {
    return !!node.children && node.children.length > 0;
  }

  public middleClickHandle(url: string, event: MouseEvent): void {
    if (event.which === 2 || event.buttons === 4) {
      Helper.openInNewTab(url, this.router);
    }
  }

  public onHover(event: MouseEvent): void {
    const sidebarMenu = document.getElementById('sidenav-hover');

    if (sidebarMenu) {
      switch (event.type) {
        case 'mouseenter':
          this.hoverTimeOut = setTimeout((): void => {
            sidebarMenu.classList.add('hover');
          }, 500);
          break;

        case 'mouseleave':
          if (this.hoverTimeOut) {
            clearTimeout(this.hoverTimeOut);
          }
          sidebarMenu.classList.remove('hover');
      }
    }
  }

  public onChildClick(node: MatTreeNode): void {
    this.nodeData = node;
    if (this.nodeData) {
      switch (this.nodeData.type) {
        case EnumSideNavTypes.LINK:
          if (this.nodeData.options?.routerLink?.[0]) {
            this.router.navigate([this.nodeData.options.routerLink[0]]);
          }
          break;
        case EnumSideNavTypes.BUTTON:
          break;
        default:
          console.log('unknown type');
          break;
      }
    }
  }

  public onToggle(isExpanded: boolean): void {
    for (let i = 0; i < this.noOfChildren; i++) {
      const childMenu = document.getElementById('childMenu-' + i);
      if (childMenu) {
        if (isExpanded) {
          childMenu.classList.add('onToggle');
        } else {
          childMenu.classList.remove('onToggle');
        }
      }
    }
  }

  private updateActiveState(url: string): void {
    const rawData = this.sideNavData.sideNavDataSource;
    for (const parent of rawData) {
      if (parent.children) {
        for (const child of parent.children) {
          const element = document.getElementById('child-' + child.index);
          if (element) {
            const routerLink = child.options?.routerLink;
            const linkStr = Array.isArray(routerLink)
              ? (routerLink[0] as string)
              : (routerLink as string);

            const cleanUrl = url.split('?')[0].split('#')[0].replace(/^\/+/, '');
            const cleanLink = (linkStr ?? '').replace(/^\/+/, '');
            const isActive =
              !!cleanLink && (cleanUrl === cleanLink || cleanUrl.startsWith(cleanLink + '/'));
            element.classList.toggle('active', isActive);

            if (isActive) {
              this.nodeData = child;
            }
          }
        }
      }
    }
  }

  private getEnvironmentFromBrowser(): void {
    const hostname = window.location ? window.location.hostname : '';
    if (hostname.split('.')[0] !== 'prod') {
      this.env2.set(hostname.split('.')[0]);
    }
  }

  private catchSetUserRightsSuccess(): void {
    this.actions$
      .pipe(ofActionDispatched(SetUserRightsSuccess), takeUntil(this.ngUnsubscribe))
      .subscribe((res: SetUserRightsSuccess): void => {
        this.userRights = res.userRights.menuAccessRightList ?? [];
        this.setItemsAccess();
      });
  }
}
